import { X } from "lucide-react";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { DocumentVersionStatus } from "~/config/types";
import { formatDate } from "~/helpers/date";
import CheckboxWithLabel from "~/molecules/CheckboxWithLabel";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import DatePicker from "~/molecules/DatePicker";
import type { DocumentVersionFiltersModel } from "../../hooks/useDocumentVersionFilters";
import {
  FilterOperatorEnum,
  type FilterCondition,
  type FilterField,
  type NullableDateFilterKey,
  filterFields,
  filterIsDateByField,
  isNullableDateFilterKey,
  type MultiSelectFilterKey,
  toDateKey,
} from "../../helpers/documentFilters";

interface FilterBarProps {
  filters: DocumentVersionFiltersModel;
}

interface ActiveFilterChipProps {
  label: string;
  comparatorLabel: string;
  value: string;
  onRemove: () => void;
  clearAriaLabel: string;
}

interface ActiveFilterItem {
  key: string;
  label: string;
  comparatorLabel: string;
  value: string;
  onRemove: () => void;
}

const getMultiSelectValueLabel = ({
  field,
  value,
  t,
  filters,
}: {
  field: MultiSelectFilterKey;
  value: string;
  t: TFunction;
  filters: DocumentVersionFiltersModel;
}) => {
  if (field === "status") {
    return t(($) => $.commons.documentStatus[value as DocumentVersionStatus]);
  }

  return filters.multiSelectFieldData[field].labelsByValue[value] ?? value;
};

const ActiveFilterChip = ({
  label,
  comparatorLabel,
  value,
  onRemove,
  clearAriaLabel,
}: ActiveFilterChipProps) => (
  <div className="border-border flex gap-1 rounded-md border px-1 text-sm font-normal">
    <span className="p-1">{label}</span>
    <span className="text-secondary-foreground bg-secondary px-2 py-1">
      {comparatorLabel}
    </span>
    <span className="p-1">{value}</span>
    <button
      type="button"
      onClick={onRemove}
      className="text-muted-foreground hover:text-foreground"
      aria-label={clearAriaLabel}
    >
      <X className="size-3" />
    </button>
  </div>
);

export const FilterBar = ({ filters }: FilterBarProps) => {
  const { t } = useTranslation();
  const comparatorLabels: Record<FilterCondition["operator"], string> = {
    gte: t(($) => $.library.filters.isAfter),
    lte: t(($) => $.library.filters.isBefore),
    eq: t(($) => $.library.filters.equals),
    in: t(($) => $.library.filters.includes),
    isNull: t(($) => $.library.filters.equals),
  };
  const getComparatorLabel = (operators: FilterCondition["operator"][]) => {
    if (
      operators.length === 2 &&
      operators.includes(FilterOperatorEnum.gte) &&
      operators.includes(FilterOperatorEnum.lte)
    ) {
      return t(($) => $.library.filters.isBetween);
    }

    if (operators.length === 1) {
      const [operator] = operators;
      if (!operator) {
        throw new Error("Missing comparator operator");
      }
      return comparatorLabels[operator];
    }

    throw new Error(
      `Unsupported comparator operator combination: ${operators.join(",")}`,
    );
  };

  const formatDateValue = (dateString?: string) => {
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? "" : formatDate(date);
  };

  const formatFilterValue = (filter: FilterCondition) => {
    const { field } = filter;

    if (filter.operator === FilterOperatorEnum.isNull) {
      return t(($) => $.library.filters.notSet);
    }

    if (filterIsDateByField(field)) {
      return typeof filter.value === "string"
        ? formatDateValue(filter.value)
        : "";
    }

    if (Array.isArray(filter.value)) {
      return filter.value
        .map((value) =>
          getMultiSelectValueLabel({
            field,
            value,
            t,
            filters,
          }),
        )
        .join(", ");
    }

    return getMultiSelectValueLabel({
      field,
      value: filter.value,
      t,
      filters,
    });
  };
  const createActiveFilterItem = ({
    key,
    label,
    comparator,
    value,
    onRemove,
  }: {
    key: string;
    label: string;
    comparator: string;
    value: string;
    onRemove: () => void;
  }): ActiveFilterItem => ({
    key,
    label,
    comparatorLabel: comparator,
    value,
    onRemove,
  });
  const filtersByField = filters.activeFilters.reduce<
    Partial<Record<FilterField, FilterCondition[]>>
  >((acc, filter) => {
    acc[filter.field] = [...(acc[filter.field] ?? []), filter];
    return acc;
  }, {});
  const activeFilterItems = filterFields.flatMap((field) => {
    const fieldFilters = filtersByField[field] ?? [];
    const gteFilter = fieldFilters.find(
      (filter) => filter.operator === FilterOperatorEnum.gte,
    );
    const lteFilter = fieldFilters.find(
      (filter) => filter.operator === FilterOperatorEnum.lte,
    );

    if (gteFilter && lteFilter) {
      const gteValue = formatFilterValue(gteFilter);
      const lteValue = formatFilterValue(lteFilter);
      return [
        createActiveFilterItem({
          key: `${field}-range`,
          label: t(($) => $.commons.document[field]),
          comparator: getComparatorLabel([
            FilterOperatorEnum.gte,
            FilterOperatorEnum.lte,
          ]),
          value: `${gteValue} - ${lteValue}`,
          onRemove: () => filters.removeFilter(field),
        }),
      ];
    }

    return fieldFilters.map((filter) =>
      createActiveFilterItem({
        key: `${field}-${filter.operator}`,
        label: t(($) => $.commons.document[field]),
        comparator: getComparatorLabel([filter.operator]),
        value: formatFilterValue(filter),
        onRemove: () => filters.removeFilter(field),
      }),
    );
  });

  if (activeFilterItems.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {activeFilterItems.map((filterItem) => (
        <ActiveFilterChip
          key={filterItem.key}
          label={filterItem.label}
          comparatorLabel={filterItem.comparatorLabel}
          value={filterItem.value}
          onRemove={filterItem.onRemove}
          clearAriaLabel={t(($) => $.commons.filter.clear)}
        />
      ))}
    </div>
  );
};

const getNullFilterLabels = (
  t: TFunction,
): Record<NullableDateFilterKey, string> => ({
  adoptedAt: t(($) => $.library.filters.nullLabel.adoptedAt),
  reviewFrom: t(($) => $.library.filters.nullLabel.reviewFrom),
  reviewTo: t(($) => $.library.filters.nullLabel.reviewTo),
});

export const FiltersSidebar = ({ filters }: FilterBarProps) => {
  const { t } = useTranslation();
  const nullFilterLabels = getNullFilterLabels(t);
  const getMultiSelectOptions = (field: MultiSelectFilterKey) =>
    filters.multiSelectFieldData[field].values.map((value) => ({
      value,
      label: getMultiSelectValueLabel({
        field,
        value,
        t,
        filters,
      }),
    }));

  const getMultiSelectPlaceholder = (
    selectedCount: number,
    fallbackPlaceholder: string,
  ) =>
    selectedCount === 0
      ? fallbackPlaceholder
      : t(($) => $.library.filters.selectedCount, {
          count: selectedCount,
        });

  const toDateFromKey = (dateKey?: string) => {
    if (!dateKey) {
      return undefined;
    }
    const date = new Date(dateKey);
    return Number.isNaN(date.getTime()) ? undefined : date;
  };

  return (
    <aside className="bg-background flex h-full min-h-0 flex-col rounded-lg border">
      <div className="min-h-0 flex-1 space-y-4 p-4">
        {filters.multiSelectFilterKeys.map((field) => {
          const selectedValues = filters.getInValues(field);

          return (
            <div key={field} className="space-y-1">
              <p className="text-sm font-medium">
                {t(($) => $.commons.document[field])}
              </p>
              <ControlledCombobox
                isMulti
                value={selectedValues}
                options={getMultiSelectOptions(field)}
                placeholder={getMultiSelectPlaceholder(
                  selectedValues.length,
                  t(($) => $.commons.select),
                )}
                onChange={(values) => filters.setInValues(field, values)}
              />
            </div>
          );
        })}

        {filters.dateFilterKeys.map((filterKey) => {
          const nullableKey = isNullableDateFilterKey(filterKey)
            ? filterKey
            : undefined;
          const nullActive =
            nullableKey !== undefined &&
            filters.isNullFilterActive(nullableKey);

          return (
            <div key={filterKey} className="space-y-1">
              <p className="text-sm font-medium">
                {t(($) => $.commons.document[filterKey])}
              </p>
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-[3.5rem_1fr] items-center gap-2">
                  <span className="text-sm">
                    {t(($) => $.library.filters.after)}
                  </span>
                  <DatePicker
                    disabled={nullActive}
                    value={toDateFromKey(
                      filters.getFilterValue(filterKey, FilterOperatorEnum.gte),
                    )}
                    onChange={(date) =>
                      filters.setFilterValue(
                        filterKey,
                        FilterOperatorEnum.gte,
                        date ? (toDateKey(date) ?? undefined) : undefined,
                      )
                    }
                  />
                </div>
                <div className="grid grid-cols-[3.5rem_1fr] items-center gap-2">
                  <span className="text-sm">
                    {t(($) => $.library.filters.before)}
                  </span>
                  <DatePicker
                    disabled={nullActive}
                    value={toDateFromKey(
                      filters.getFilterValue(filterKey, FilterOperatorEnum.lte),
                    )}
                    onChange={(date) =>
                      filters.setFilterValue(
                        filterKey,
                        FilterOperatorEnum.lte,
                        date ? (toDateKey(date) ?? undefined) : undefined,
                      )
                    }
                  />
                </div>
                {filters.isDateRangeInvalid(filterKey) && (
                  <p className="text-destructive text-sm">
                    {t(($) => $.library.filters.invalidDateRange)}
                  </p>
                )}
                {nullableKey !== undefined && (
                  <CheckboxWithLabel
                    id={`${nullableKey}-null`}
                    label={nullFilterLabels[nullableKey]}
                    checked={filters.isNullFilterActive(nullableKey)}
                    onCheckedChange={(checked) =>
                      filters.setNullFilter(nullableKey, checked)
                    }
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};
